import Moment from 'moment';

import { ServiceHandler } from '../../utils';
import { getInsights } from '../insights/InsightsService';
import { ActiveTab } from '../insights/spotlight-details/spotlightDetails.types';
import { adaptListItem } from './spotlight.adapt';
import type {
  CustomerSpotlight,
  CustomerSpotlightListParams,
  CustomerSpotlightListResponse,
  CustomerSpotlightsDelegateResult,
  CustomerSpotlightsTableQuery,
  CustomerSpotlightFeedbackRequest,
} from './spotlights.types';

const getCustomerSpotlights = ServiceHandler<
  unknown,
  CustomerSpotlightListResponse
>({
  url: '/api/resilience/v1/customer-spotlights',
  method: 'GET',
});

export const getCustomerSpotlightById = ServiceHandler<
  unknown,
  CustomerSpotlight
>({
  url: '/api/resilience/v1/customer-spotlights/:id',
  method: 'GET',
});

export const submitCustomerSpotlightFeedback = ServiceHandler<
  CustomerSpotlightFeedbackRequest,
  void
>({
  url: '/api/resilience/v1/customer-spotlight-feedback',
  method: 'POST',
});

export const requestCyberExpertContactEmail = ServiceHandler<unknown, void>({
  url: '/api/resilience/v1/customer-spotlights/:customerSpotlightId/contact-cyber-expert/:spotlightProductDetailsId',
  method: 'POST',
});

function transformBeforeParam(before?: number): string {
  if (before) {
    return Moment.unix(before).endOf('day').toISOString();
  }

  return Moment().endOf('day').toISOString();
}

function transformAfterParam(after?: number): string | null {
  if (!after) {
    return null;
  }

  return Moment.unix(after).startOf('day').toISOString();
}

// The Date column's UI-facing name is `created` (see TabbedReasonsRecommendations'
// useColumnConfig), but the PG-backed CustomerSpotlight entity's own date field is
// `createdTs` — the response is remapped to `created` only for display (spotlight.adapt.ts).
// Sorting sent the display name straight through as the backend sort property, which the
// entity doesn't expose under that name, so the Date column never sorted correctly.
const SORT_FIELD_OVERRIDES: Record<string, string> = {
  created: 'createdTs',
};

function buildSortParam(orderBy?: string, order?: string): string | undefined {
  if (!orderBy) {
    return undefined;
  }

  const sortField = SORT_FIELD_OVERRIDES[orderBy] ?? orderBy;
  return `${sortField},${order ?? 'desc'}`;
}

function buildCustomerSpotlightListParams(
  accountId: string,
  tableQuery: CustomerSpotlightsTableQuery
): CustomerSpotlightListParams {
  return {
    accountId,
    description: tableQuery.search,
    before: transformBeforeParam(tableQuery.before),
    after: transformAfterParam(tableQuery.after) ?? undefined,
    page: tableQuery.page ?? 0,
    size: tableQuery.size ?? 25,
    sort: buildSortParam(tableQuery.orderBy, tableQuery.order),
  };
}

export function fetchCustomerSpotlightCount(
  accountId: string
): Promise<number> {
  return getCustomerSpotlights({
    params: { accountId, page: 0, size: 1 },
  }).then(({ data }) => data.totalElements);
}

export function fetchCustomerSpotlightsList(
  accountId: string,
  tableQuery: CustomerSpotlightsTableQuery
): Promise<CustomerSpotlightsDelegateResult> {
  const params = buildCustomerSpotlightListParams(accountId, tableQuery);

  return getCustomerSpotlights({ params }).then(({ data }) => ({
    data: data.content.map(adaptListItem),
    count: data.totalElements,
  }));
}

export function normalizeLegacyVendorAdvisoryLinks(
  item: Record<string, unknown>
): Record<string, unknown> {
  const { vendorAdvisoryLink, vendorAdvisoryLinks, ...rest } = item;

  return {
    ...rest,
    vendorAdvisoryLinks:
      (vendorAdvisoryLinks as string[] | undefined) ??
      (vendorAdvisoryLink ? [vendorAdvisoryLink as string] : []),
  };
}

export function fetchLegacySpotlightInsights(
  accountId: string,
  tableQuery: CustomerSpotlightsTableQuery
): Promise<CustomerSpotlightsDelegateResult> {
  return getInsights({
    accountId,
    ...tableQuery,
    recommendationTab: ActiveTab.SPOTLIGHT,
  }).then(({ data }) => ({
    data: data.content.map((item: Record<string, unknown>) => ({
      ...normalizeLegacyVendorAdvisoryLinks(item),
      insightId: item.id,
      id: item.findingId || item.reasonName || item.reasonDescription,
    })),
    count: data.totalElements,
  }));
}
